import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getReservation,
  confirmReservation,
  rejectReservation,
  cancelReservation,
  completeReservation,
  openInvoicePdf,
  markInvoicePaid,
  resendInvoice,
  regenerateInvoice,
  ReservationDetail as TReservationDetail,
  ReservationStatus,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";
import { czLongDate } from "../../../lib/dates";

export default function ReservationDetail() {
  const { id } = useParams();
  const toast = useToast();
  const [r, setR] = useState<TReservationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  function reload() {
    if (!id) return;
    setLoading(true);
    getReservation(id)
      .then(setR)
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
  }
  useEffect(reload, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onConfirm() {
    if (!r) return;
    if (
      !confirm(
        `Potvrdit rezervaci "${r.snapshotName}" na ${czLongDate(new Date(r.date))}? Klient dostane potvrzovací e-mail (faktura/doklad se vystaví až po označení akce jako proběhlé).`
      )
    )
      return;
    setWorking(true);
    try {
      const res = await confirmReservation(r.id);
      setR(res.reservation);
      toast.show(
        res.emailSent ? "success" : "info",
        res.emailSent
          ? "Rezervace potvrzena + potvrzovací e-mail odeslán."
          : `Potvrzeno, ale e-mail selhal: ${res.emailError ?? "neznámá chyba"}`
      );
      reload();
    } catch (e: any) {
      toast.show("error", e.message ?? "Potvrzení selhalo.");
    } finally {
      setWorking(false);
    }
  }

  async function onOpenInvoice() {
    if (!r?.invoice) return;
    try {
      await openInvoicePdf(r.invoice.id);
    } catch (e: any) {
      toast.show("error", e.message ?? "PDF nejde stáhnout.");
    }
  }

  async function onMarkPaid() {
    if (!r?.invoice) return;
    const nextPaid = !r.invoice.paid;
    try {
      await markInvoicePaid(r.invoice.id, nextPaid);
      toast.show("success", nextPaid ? "Označeno jako zaplacené." : "Označeno jako nezaplacené.");
      reload();
    } catch (e: any) {
      toast.show("error", e.message ?? "Akce selhala.");
    }
  }

  async function onResendInvoice() {
    if (!r?.invoice) return;
    if (!confirm("Znovu poslat fakturu klientovi e-mailem?")) return;
    setWorking(true);
    try {
      await resendInvoice(r.invoice.id);
      toast.show("success", "Faktura znovu odeslána.");
      reload();
    } catch (e: any) {
      toast.show("error", e.message ?? "Odeslání selhalo.");
    } finally {
      setWorking(false);
    }
  }

  async function onRegenerateInvoice() {
    if (!r?.invoice) return;
    if (!confirm("Přegenerovat PDF faktury? Použije se aktuální stav rezervace.")) return;
    setWorking(true);
    try {
      await regenerateInvoice(r.invoice.id);
      toast.show("success", "PDF přegenerováno.");
      reload();
    } catch (e: any) {
      toast.show("error", e.message ?? "Přegenerování selhalo.");
    } finally {
      setWorking(false);
    }
  }

  async function onReject() {
    if (!r) return;
    const reason = prompt("Důvod zamítnutí (zobrazí se klientovi v e-mailu — nebo nechte prázdné):") ?? "";
    // null = zrušeno přes dialog → neprováděj akci
    if (reason === null) return;
    setWorking(true);
    try {
      const res = await rejectReservation(r.id, reason || undefined, true);
      setR(res.reservation);
      toast.show(
        res.emailSent ? "success" : "error",
        res.emailSent ? "Zamítnuto + e-mail odeslán." : `Zamítnuto, ale e-mail selhal: ${res.emailError}`
      );
    } catch (e: any) {
      toast.show("error", e.message ?? "Zamítnutí selhalo.");
    } finally {
      setWorking(false);
    }
  }

  async function onCancel() {
    if (!r) return;
    const reason = prompt("Důvod zrušení rezervace:") ?? "";
    if (reason === null) return;
    if (!confirm("Opravdu zrušit už potvrzenou rezervaci? Sloty se uvolní.")) return;
    setWorking(true);
    try {
      const res = await cancelReservation(r.id, reason || undefined);
      setR(res.reservation);
      toast.show("success", "Rezervace zrušena, sloty uvolněny.");
    } catch (e: any) {
      toast.show("error", e.message ?? "Zrušení selhalo.");
    } finally {
      setWorking(false);
    }
  }

  async function onComplete() {
    if (!r) return;
    const docName = r.paymentMethod === "CASH" ? "pokladní doklad" : "faktura";
    if (
      !confirm(
        `Označit rezervaci jako proběhlou? Vygeneruje se ${docName} a pošle klientovi e-mailem s poděkováním.`
      )
    )
      return;
    setWorking(true);
    try {
      const res = await completeReservation(r.id);
      setR(res.reservation);
      const parts: string[] = [];
      parts.push("Označeno jako proběhlé.");
      if (res.documentNumber) {
        parts.push(
          `${res.documentType === "CASH_RECEIPT" ? "Pokladní doklad" : "Faktura"} ${res.documentNumber} vytvořena.`
        );
      } else if (res.documentError) {
        parts.push(`⚠️ Doklad: ${res.documentError}`);
      }
      if (res.emailSent) parts.push("E-mail odeslán.");
      else if (res.emailError) parts.push(`⚠️ E-mail: ${res.emailError}`);
      toast.show(res.emailSent && res.documentNumber ? "success" : "info", parts.join(" "));
      reload();
    } catch (e: any) {
      toast.show("error", e.message ?? "Selhalo.");
    } finally {
      setWorking(false);
    }
  }

  if (loading || !r) return <div className="text-slate-500">Načítám…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/reservations" className="text-sm text-slate-500 hover:text-slate-700">
          ← Zpět na rezervace
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{r.snapshotName}</h1>
            <div className="mt-1 text-slate-600">
              {czLongDate(new Date(r.date))} · {r.startTime}–{r.endTime}
            </div>
          </div>
          <StatusBadge status={r.status} />
        </div>
      </div>

      {/* Akce podle stavu */}
      <div className="card flex flex-wrap gap-3">
        {r.status === "PENDING" && (
          <>
            <button onClick={onConfirm} disabled={working} className="btn-primary">
              ✓ Potvrdit
            </button>
            <button
              onClick={onReject}
              disabled={working}
              className="rounded-lg border border-red-300 bg-white px-4 py-2 font-medium text-red-700 hover:bg-red-50"
            >
              ✕ Zamítnout
            </button>
          </>
        )}
        {r.status === "CONFIRMED" && (
          <>
            <button onClick={onComplete} disabled={working} className="btn-primary">
              ✓ Označit jako proběhlé
            </button>
            <button
              onClick={onCancel}
              disabled={working}
              className="rounded-lg border border-red-300 bg-white px-4 py-2 font-medium text-red-700 hover:bg-red-50"
            >
              Zrušit rezervaci
            </button>
          </>
        )}
        {r.status === "COMPLETED" && <div className="text-sm text-slate-500">Rezervace proběhla.</div>}
        {r.status === "CANCELLED" && (
          <div className="text-sm text-slate-500">
            Rezervace zrušena.
            {r.cancelReason && <div className="mt-1 text-xs">Důvod: {r.cancelReason}</div>}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Levý sloupec — detaily */}
        <div className="space-y-6 lg:col-span-2">
          <Section title="Kontakt">
            <InfoRow label="Jméno" value={r.contactName} />
            <InfoRow label="E-mail" value={<a href={`mailto:${r.contactEmail}`} className="text-obloha-indigo-700 hover:underline">{r.contactEmail}</a>} />
            {r.contactPhone && <InfoRow label="Telefon" value={<a href={`tel:${r.contactPhone}`} className="text-obloha-indigo-700 hover:underline">{r.contactPhone}</a>} />}
            <InfoRow
              label="Platba"
              value={
                r.paymentMethod === "CASH" ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    💵 <strong>Hotově v den akce</strong>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    🧾 <strong>Na fakturu</strong>
                  </span>
                )
              }
            />
            {r.note && <InfoRow label="Poznámka" value={<div className="whitespace-pre-wrap">{r.note}</div>} />}
          </Section>

          <Section title="Adresa">
            <div className="text-slate-700">
              <div>{r.snapshotStreet}</div>
              <div>
                {r.snapshotZip} {r.snapshotCity}
              </div>
              {r.distanceKm != null && (
                <div className="mt-2 text-sm text-slate-500">
                  {r.distanceKm} km z našeho sídla
                  {r.durationMin != null && ` (cca ${r.durationMin} min jízdy)`}
                </div>
              )}
              {r.snapshotLat != null && r.snapshotLon != null && (
                <a
                  href={`https://mapy.cz/zakladni?x=${r.snapshotLon}&y=${r.snapshotLat}&z=16&source=coor&id=${r.snapshotLon},${r.snapshotLat}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm text-obloha-indigo-700 hover:underline"
                >
                  Otevřít v Mapy.cz →
                </a>
              )}
            </div>
          </Section>

          <Section title="Programy">
            <ul className="space-y-3">
              {r.programs.map((pi) => (
                <li key={pi.id} className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-slate-100">
                    {pi.program.coverImageUrl ? (
                      <img src={pi.program.coverImageUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-lg">✨</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{pi.program.name}</div>
                    <div className="text-xs text-slate-500">
                      {pi.childrenCount} dětí · {pi.blocksNeeded}× {pi.snapshotDurationMinutes} min
                      {pi.blocksNeeded > 1 && <span className="ml-1 text-amber-600">(opakování)</span>}
                    </div>
                  </div>
                  <div className="text-right tabular-nums font-medium">
                    {pi.computedPriceCzk.toLocaleString("cs-CZ")} Kč
                  </div>
                </li>
              ))}
            </ul>
          </Section>

          <Section title={`Obsazené sloty (${r.slots.length} × 30 min)`}>
            <div className="flex flex-wrap gap-2">
              {r.slots.map((s) => (
                <span
                  key={s.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700"
                >
                  {s.startTime}–{s.endTime}
                </span>
              ))}
            </div>
          </Section>
        </div>

        {/* Pravý sloupec — ceny */}
        <div className="space-y-4">
          <Section title="Rozpis ceny">
            <dl className="space-y-2 text-sm">
              <PriceRow label="Programy" value={r.programsPriceCzk} />
              <PriceRow label="Doprava" value={r.travelPriceCzk} />
              <div className="border-t border-slate-200 pt-2 text-base font-bold">
                <PriceRow label="Celkem" value={r.totalPriceCzk} highlight />
              </div>
            </dl>
          </Section>

          <Section title="Časová osa">
            <TimelineRow label="Přišla" value={new Date(r.createdAt).toLocaleString("cs-CZ")} />
            {r.confirmedAt && (
              <TimelineRow label="Potvrzená" value={new Date(r.confirmedAt).toLocaleString("cs-CZ")} />
            )}
            {r.completedAt && (
              <TimelineRow label="Proběhla" value={new Date(r.completedAt).toLocaleString("cs-CZ")} />
            )}
          </Section>

          {r.invoice && (
            <Section title="Faktura">
              <div className="space-y-3">
                <div>
                  <div className="text-lg font-semibold text-obloha-indigo-900">
                    {r.invoice.number}
                  </div>
                  <div className="text-xs text-slate-500">
                    VS {r.invoice.variableSymbol} · splatnost{" "}
                    {new Date(r.invoice.dateDue).toLocaleDateString("cs-CZ")}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {r.invoice.paid ? (
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
                      ✓ Zaplaceno
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                      Čeká na platbu
                    </span>
                  )}
                  {r.invoice.sentAt && (
                    <span className="text-xs text-slate-500">
                      odesláno {new Date(r.invoice.sentAt).toLocaleDateString("cs-CZ")}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  <button onClick={onOpenInvoice} className="btn-primary w-full text-sm">
                    🧾 Stáhnout / otevřít PDF
                  </button>
                  <button
                    onClick={onMarkPaid}
                    className={`w-full rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      r.invoice.paid
                        ? "border-amber-300 bg-white text-amber-700 hover:bg-amber-50"
                        : "border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"
                    }`}
                  >
                    {r.invoice.paid ? "↻ Označit jako nezaplacené" : "✓ Označit jako zaplacené"}
                  </button>
                  <button
                    onClick={onResendInvoice}
                    disabled={working}
                    className="btn-secondary w-full text-sm"
                  >
                    ✉ Poslat znovu klientovi
                  </button>
                  <button
                    onClick={onRegenerateInvoice}
                    disabled={working}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    Přegenerovat PDF
                  </button>
                </div>
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── podsložky ────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="mb-4 text-base font-semibold text-slate-900">{title}</h2>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="mb-2 last:mb-0 grid grid-cols-3 gap-2 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="col-span-2 text-slate-900">{value}</dd>
    </div>
  );
}

function PriceRow({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={highlight ? "text-slate-900" : "text-slate-600"}>{label}</span>
      <span className={`tabular-nums ${highlight ? "text-obloha-indigo-800" : "text-slate-900"}`}>
        {value.toLocaleString("cs-CZ")} Kč
      </span>
    </div>
  );
}

function TimelineRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-xs text-slate-500">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: ReservationStatus }) {
  const styles: Record<ReservationStatus, string> = {
    PENDING: "bg-amber-100 text-amber-800 border-amber-200",
    CONFIRMED: "bg-obloha-indigo-100 text-obloha-indigo-800 border-obloha-indigo-200",
    COMPLETED: "bg-emerald-100 text-emerald-800 border-emerald-200",
    CANCELLED: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const labels: Record<ReservationStatus, string> = {
    PENDING: "Čeká na schválení",
    CONFIRMED: "Potvrzená",
    COMPLETED: "Proběhlá",
    CANCELLED: "Zrušená",
  };
  return (
    <span className={`rounded-full border px-3 py-1 text-sm font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}
